import React from "react";
import { Link as RemixLink } from "@remix-run/react";
import { TapTooltip } from "#app/components/TapTooltip.jsx";
import {
  format,
  formatDistanceToNowStrict,
  parseISO,
  differenceInMinutes,
  differenceInHours,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  Badge,
  Flex,
  Icon,
  Tag,
  TagRightIcon,
  TagLabel,
  Text,
  Tooltip,
  useConst,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Box,
  HStack,
  Link,
  Wrap,
} from "@chakra-ui/react";

import { CalendarIcon as ArrivalIcon } from "@chakra-ui/icons";
import {
  MdOutlineNightsStay as NightsIcon,
  MdHiking as TripIcon,
  MdGroup as CapacityIcon,
} from "react-icons/md";
import { FaCampground as SitesIcon } from "react-icons/fa";

import { getSiteTypeLabel, getSiteFacetLabel } from "../../utils/labels.js";
import { useLocale } from "#app/context/LocaleContext.jsx";

import {
  includeNightsText,
  includeCapacityText,
  nightsText,
  sitesText,
  capacityText,
  getCapacityUnit,
  groupConsecutiveDates,
  formatDateGroup,
} from "#app/utils/availabilityRequest.js";

import { maxRVLengthOptions } from "#app/utils/maps/index.js";

import { useUserSubscriptionValue } from "#app/hooks/index.js";

import {
  AccessibleIcon,
  WaterIcon,
  SewerIcon,
  ElectricIcon,
  PullThroughIcon,
  TentOnlyIcon,
  DoubleSiteIcon,
} from "./Icons.jsx";

function RightIconTag({ Icon, text, ...props }) {
  return (
    <Tag variant="subtle" size="sm" {...props}>
      <TagLabel>{text}</TagLabel>
      <TagRightIcon fontSize="md" as={Icon} />
    </Tag>
  );
}

function SitesDetailModal({ isOpen, onClose, availabilityRequest, country }) {
  const siteCount = availabilityRequest.selectedSiteIds?.length || 0;

  if (siteCount === 0) {
    return null;
  }

  const hasFullSiteData =
    availabilityRequest.sites &&
    availabilityRequest.sites.length > 0 &&
    availabilityRequest.sites[0].name != null;

  if (!hasFullSiteData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{siteCount.toLocaleString()} Selected Sites</Text>
            <Wrap mt={2} spacing={2} wrap="wrap">
              <SiteTypeBadge
                type={availabilityRequest.siteType}
                country={country}
              />
              <SiteFilterBadge site={availabilityRequest} country={country} />
            </Wrap>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="start" spacing={4}>
              <Text color="muted">
                Site details are available when viewing the full request.
              </Text>
              <Link
                as={RemixLink}
                to={`/r/${availabilityRequest.uuid}`}
                color="accent"
              >
                View full request details →
              </Link>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const collator = new Intl.Collator("en", {
    numeric: true,
    sensitivity: "base",
  });

  // Sort function that handles time-based site names (e.g., "10:30am", "2:00pm")
  const sortSites = (a, b) => {
    // For Melanzana time slots, extract time and convert to 24h for sorting
    const timeRegex = /^(\d{1,2}):(\d{2})(am|pm)$/;
    const matchA = a.name.match(timeRegex);
    const matchB = b.name.match(timeRegex);

    if (matchA && matchB) {
      // Both are time slots - parse and compare
      let hourA = parseInt(matchA[1], 10);
      let hourB = parseInt(matchB[1], 10);
      const minA = parseInt(matchA[2], 10);
      const minB = parseInt(matchB[2], 10);

      // Convert to 24h format
      if (matchA[3] === "pm" && hourA !== 12) hourA += 12;
      if (matchA[3] === "am" && hourA === 12) hourA = 0;
      if (matchB[3] === "pm" && hourB !== 12) hourB += 12;
      if (matchB[3] === "am" && hourB === 12) hourB = 0;

      // Compare by hour, then by minute
      if (hourA !== hourB) return hourA - hourB;
      return minA - minB;
    }

    // Fall back to natural sort for non-time sites
    return collator.compare(a.name, b.name);
  };

  const sitesByGroup = availabilityRequest.sites
    .map((site) => ({
      ...site,
      group: site.group || "All Sites",
    }))
    .sort(sortSites)
    .reduce((acc, site) => {
      if (!acc[site.group]) {
        acc[site.group] = [];
      }
      acc[site.group].push(site);
      return acc;
    }, {});

  const sortedGroups = Object.keys(sitesByGroup).sort((a, b) => {
    if (a === "All Sites") return 1;
    if (b === "All Sites") return -1;
    return collator.compare(a, b);
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{siteCount.toLocaleString()} Selected Sites</Text>
          <Wrap mt={2} spacing={2} wrap="wrap">
            <SiteTypeBadge
              type={availabilityRequest.siteType}
              country={country}
            />
            <SiteFilterBadge site={availabilityRequest} country={country} />
          </Wrap>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} maxH="500px" overflowY="auto">
          <VStack align="start" spacing={6} w="full">
            {sortedGroups.map((groupName) => {
              const sites = sitesByGroup[groupName];
              return (
                <Box key={groupName} w="full">
                  <HStack mb={3} justify="space-between">
                    <Text
                      fontWeight="semibold"
                      color="emphasized"
                      fontSize="md"
                    >
                      {groupName}
                    </Text>
                    <Badge variant="subtle" colorScheme="gray">
                      {sites.length} {sites.length === 1 ? "site" : "sites"}
                    </Badge>
                  </HStack>
                  <Box
                    border="1px"
                    borderColor="border"
                    borderRadius="md"
                    p={3}
                    bg="bg-subtle"
                  >
                    <Text fontSize="sm" color="muted" lineHeight="tall">
                      {sites.map((site, index) => (
                        <React.Fragment
                          key={`${groupName}-${site.id || site.uuid || index}`}
                        >
                          <Text as="span" fontWeight="medium">
                            {site.name}
                          </Text>
                          {index < sites.length - 1 && (
                            <Text as="span" color="subtle">
                              {" • "}
                            </Text>
                          )}
                        </React.Fragment>
                      ))}
                    </Text>
                  </Box>
                </Box>
              );
            })}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export function SiteFilterBadge({ site, country, ...props }) {
  return (
    <>
      {site.isAccessible && (
        <RightIconTag
          {...props}
          Icon={AccessibleIcon}
          text={getSiteFacetLabel("isAccessible", country)}
        />
      )}
      {(site.isTentOnly || site.type === "tent") && (
        <RightIconTag
          {...props}
          Icon={TentOnlyIcon}
          text={getSiteFacetLabel("isTentOnly", country)}
        />
      )}
      {site.isDoubleSite && (
        <RightIconTag
          {...props}
          Icon={DoubleSiteIcon}
          text={getSiteFacetLabel("isDoubleSite", country)}
        />
      )}
      {site.maxRVLength &&
        maxRVLengthOptions.find(
          (option) => option.value == site.maxRVLength,
        ) && (
          <Tag size="sm" {...props}>{`${
            maxRVLengthOptions.find(
              (option) => option.value == site.maxRVLength,
            ).label
          }`}</Tag>
        )}
      {site.hasElectric ? (
        site.maxElectric ? (
          <RightIconTag
            {...props}
            Icon={ElectricIcon}
            text={`${site.maxElectric} Amps`}
          />
        ) : (
          <RightIconTag
            {...props}
            Icon={ElectricIcon}
            text={getSiteFacetLabel("hasElectric", country)}
          />
        )
      ) : null}
      {site.hasWater && (
        <RightIconTag
          {...props}
          Icon={WaterIcon}
          text={getSiteFacetLabel("hasWater", country)}
        />
      )}
      {site.hasSewer && (
        <RightIconTag
          {...props}
          Icon={SewerIcon}
          text={getSiteFacetLabel("hasSewer", country)}
        />
      )}
      {site.isPullThrough && (
        <RightIconTag
          {...props}
          Icon={PullThroughIcon}
          text={getSiteFacetLabel("isPullThrough", country).replace(
            / Only$/,
            "",
          )}
        />
      )}
    </>
  );
}

export function SiteTypeBadge({ type, country, ...badgeProps }) {
  const badgeText = getSiteTypeLabel(type, country);
  return (
    <Tag size="sm" {...badgeProps}>
      {badgeText}
    </Tag>
  );
}

export function AvailabilityMatchUnavailableAtBadge({
  availabilityMatch,
  ...badgeProps
}) {
  const now = useConst(new Date());
  const unavailableAt = formatDistanceToNowStrict(
    parseISO(availabilityMatch.unavailableAt),
    {
      addSuffix: true,
    },
  );

  const badgeText = `closed ${unavailableAt}`;
  const colorScheme = "red";

  return (
    <Badge colorScheme={colorScheme} {...badgeProps}>
      {badgeText}
    </Badge>
  );
}

function getTimeBasedBadge(checkedAt, prefix, now) {
  let badgeText = prefix;
  let colorScheme;
  const parsed = parseISO(checkedAt);

  if (differenceInMinutes(now, parsed) <= 60) {
    colorScheme = "green";
  } else if (differenceInHours(now, parsed) <= 4) {
    colorScheme = "orange";
  } else {
    colorScheme = "red";
  }

  badgeText += formatDistanceToNowStrict(parsed, { addSuffix: true });
  return { badgeText, colorScheme };
}

// Format in UTC to avoid timezone shift — these dates represent park-local calendar
// dates stored as UTC timestamps (e.g. midnight MDT = 06:00Z). Formatting in the
// browser's local timezone would show the wrong day for users west of the park.
function getGoLiveDate(sites) {
  const goLiveDate = sites?.[0]?.extraAttributes?.goLiveDate;
  if (!goLiveDate) return null;
  return formatInTimeZone(parseISO(goLiveDate), "UTC", "MMM d");
}

function getSeasonOpenDate(sites) {
  const reservationStartDate = sites?.[0]?.reservationStartDate;
  if (!reservationStartDate) return null;
  return formatInTimeZone(new Date(reservationStartDate), "UTC", "MMM d");
}

export function AvailabilityRequestCheckedAtBadge({
  availabilityRequest,
  prefix = "checked ",
  ...badgeProps
}) {
  const now = useConst(new Date());
  const { checkedCount, checkedResult, checkedAt, sites, endDate } =
    availabilityRequest;
  let badgeText, colorScheme, tooltipLabel;

  // A past-date request sits in DATES_NOT_IN_WINDOW between the scan window
  // rolling over its dates and the daily expiry job archiving it. "scanning
  // starts soon" reads as an outage there ("is the system down?") — say what
  // actually happened instead. endDate is a yyyyMMdd integer.
  const datesHavePassed =
    checkedResult === "DATES_NOT_IN_WINDOW" &&
    endDate &&
    endDate < parseInt(format(now, "yyyyMMdd"), 10);

  if (checkedResult === "SKIPPED_INACTIVE") {
    colorScheme = "gray";
    badgeText = "trial expired";
    tooltipLabel =
      "Your free trial notifications have been used up. Subscribe to resume watching.";
  } else if (checkedResult === "DATES_OUTSIDE_SEASON") {
    colorScheme = "gray";
    badgeText = "dates outside season";
    tooltipLabel =
      "Your selected dates fall outside this facility's operating season. We can't check dates outside the season.";
  } else if (datesHavePassed) {
    colorScheme = "gray";
    badgeText = "dates have passed";
    tooltipLabel =
      "The dates on this request have passed, so watching has stopped. It will be archived automatically.";
  } else if (checkedResult === "DATES_NOT_IN_WINDOW") {
    colorScheme = "gray";
    badgeText = "watching starts soon";
    tooltipLabel =
      "These dates aren't in the booking window yet. We'll start watching automatically when they are.";
  } else if (checkedResult === "PRE_GO_LIVE") {
    const dateStr = getGoLiveDate(sites);
    colorScheme = "gray";
    badgeText = dateStr ? `booking opens ${dateStr}` : "booking not yet open";
    tooltipLabel =
      "This facility's booking system isn't live yet. Watching starts when bookings open.";
  } else if (checkedResult === "SEASONAL_NOT_OPEN") {
    const dateStr = getSeasonOpenDate(sites);
    colorScheme = "gray";
    badgeText = dateStr ? `season opens ${dateStr}` : "season not yet open";
    tooltipLabel =
      "This facility isn't open for the season yet. Watching starts when the season opens.";
  } else if (checkedResult === "SEASONAL_CLOSED") {
    colorScheme = "gray";
    badgeText = "awaiting new dates";
    tooltipLabel =
      "This facility's reservation dates haven't been released yet for the upcoming season. Watching starts when new dates are available.";
  } else if (checkedCount === 0) {
    colorScheme = "orange";
    badgeText = "awaiting first check";
  } else if (checkedAt) {
    ({ badgeText, colorScheme } = getTimeBasedBadge(checkedAt, prefix, now));
  } else {
    colorScheme = "orange";
    badgeText = "awaiting first check";
  }

  const badge = (
    <Badge
      colorScheme={colorScheme}
      {...badgeProps}
      suppressHydrationWarning={true}
      {...(tooltipLabel && { cursor: "help" })}
    >
      {badgeText}
    </Badge>
  );

  if (tooltipLabel) {
    return (
      <Tooltip label={tooltipLabel} fontSize="sm" hasArrow>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

export function AvailabilityRequestStatusBadge({
  availabilityRequest,
  ...badgeProps
}) {
  const badgeText = availabilityRequest.status;
  let colorScheme;

  if (badgeText === "active") {
    colorScheme = "green";
  } else if (badgeText === "paused") {
    colorScheme = "orange";
  } else if (badgeText === "cancelled") {
    colorScheme = "red";
  } else if (badgeText === "expired") {
    colorScheme = "gray";
  } else {
    colorScheme = "gray";
  }

  return (
    <Badge
      colorScheme={colorScheme}
      {...badgeProps}
      data-testid="availability-request-status-badge"
    >
      {badgeText}
    </Badge>
  );
}

export function AvailabilityMatchStatusBadge({
  availabilityMatch,
  availabilityRequest,
  ...badgeProps
}) {
  let badgeText, colorScheme;

  if (availabilityMatch.available) {
    const status =
      availabilityRequest?.status ||
      availabilityMatch?.availabilityRequest?.status;

    const isNotScanning =
      status === "cancelled" ||
      status === "paused" ||
      availabilityMatch?.availabilityRequest?.isNotScanning;

    if (isNotScanning) {
      badgeText = "unknown";
      colorScheme = "orange";
    } else if (availabilityMatch.duplicateFromId) {
      badgeText = "re-open";
      colorScheme = "orange";
    } else {
      badgeText = "open";
      colorScheme = "green";
    }
  } else {
    colorScheme = "red";
    badgeText = "closed";
  }

  return (
    <Badge colorScheme={colorScheme} {...badgeProps}>
      {badgeText}
    </Badge>
  );
}

export function AvailabilityRequestArrivalBadge({
  availabilityRequest,
  ...props
}) {
  const locale = useLocale();
  const [showAllDates, setShowAllDates] = React.useState(false);

  const dates = availabilityRequest.selectedDates || [];
  const dateGroups = groupConsecutiveDates(dates);
  const maxVisibleGroups = 3;

  const visibleGroups = showAllDates
    ? dateGroups
    : dateGroups.slice(0, maxVisibleGroups);
  const hiddenGroupsCount = dateGroups.length - maxVisibleGroups;
  const hiddenDatesCount =
    dates.length - visibleGroups.reduce((sum, group) => sum + group.length, 0);

  return (
    <Flex alignItems="flex-start" {...props}>
      <Icon as={ArrivalIcon} boxSize="14px" mr={2} mt="4px" />
      <Text>
        {visibleGroups
          .map((group) => formatDateGroup(group, { locale }))
          .join(", ")}
        {!showAllDates && hiddenGroupsCount > 0 && (
          <Button
            variant="link"
            size="xs"
            onClick={() => setShowAllDates(true)}
            ml={1}
            fontSize="sm"
            minH="auto"
            p={0}
            colorScheme="brand"
          >
            +{hiddenDatesCount} more
          </Button>
        )}
        {showAllDates && dateGroups.length > maxVisibleGroups && (
          <Button
            variant="link"
            size="xs"
            onClick={() => setShowAllDates(false)}
            ml={1}
            fontSize="sm"
            minH="auto"
            p={0}
            colorScheme="brand"
          >
            (collapse)
          </Button>
        )}
      </Text>
    </Flex>
  );
}

export function AvailabilityRequestNightsBadge({
  availabilityRequest,
  ...props
}) {
  if (!includeNightsText(availabilityRequest)) {
    return null;
  }

  return (
    <Flex alignItems="center" {...props}>
      <Icon as={NightsIcon} boxSize="18px" mr={1} />
      <Text>
        <Text as="span">{nightsText(availabilityRequest)}</Text>
      </Text>
    </Flex>
  );
}

export function ChannelBadge({ icon, label, enabled, ...props }) {
  return (
    <Flex
      alignItems="center"
      {...props}
      fontSize="sm"
      color={enabled ? "inherit" : "border"}
      textDecoration={enabled ? "none" : "line-through"}
    >
      <Icon as={icon} boxSize="16px" mr={1} />
      <Text>
        <Text as="span">{label}</Text>
      </Text>
    </Flex>
  );
}

export function AvailabilityRequestSitesBadge({
  availabilityRequest,
  ...props
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const country = availabilityRequest.facility?.agency?.country;
  const siteCount =
    availabilityRequest._count?.sites ||
    availabilityRequest.selectedSiteIds?.length ||
    0;

  const hasFullSiteData =
    availabilityRequest.sites?.length > 0 &&
    availabilityRequest.sites[0].name != null;

  return (
    <>
      <Flex alignItems="center" {...props}>
        <Icon as={SitesIcon} boxSize="16px" mr={1} />
        <Text>
          <Text as="span">{sitesText(availabilityRequest, { country })}</Text>
          {siteCount > 1 && hasFullSiteData && (
            <Button
              variant="link"
              size="xs"
              onClick={onOpen}
              ml={1}
              fontSize="sm"
              minH="auto"
              p={0}
              colorScheme="brand"
            >
              (view all)
            </Button>
          )}
        </Text>
      </Flex>

      <SitesDetailModal
        isOpen={isOpen}
        onClose={onClose}
        availabilityRequest={availabilityRequest}
        country={country}
      />
    </>
  );
}

export function AvailabilityRequestCapacityBadge({
  availabilityRequest,
  ...props
}) {
  if (!includeCapacityText(availabilityRequest)) {
    return null;
  }

  const unit = getCapacityUnit(availabilityRequest);

  return (
    <Flex alignItems="center" {...props}>
      <Icon as={CapacityIcon} boxSize={5} mr={1} />
      <Text>
        with{" "}
        <Text as="span">{capacityText(availabilityRequest, { unit })}</Text>
      </Text>
    </Flex>
  );
}

export function SubscriptionBadge({ ...badgeProps }) {
  const defaultProps = useUserSubscriptionValue({
    none: () => null,
    trial: () => ({
      colorScheme: "orange",
      children: "trial",
    }),
    active_flexible: () => ({
      colorScheme: "blue",
      children: "legacy",
    }),
    active_unlimited: () => ({
      colorScheme: "blue",
      children: "legacy",
    }),
    active: (user) => ({
      colorScheme: "green",
      children: user.subscription.status.replace("active_", ""),
    }),
    active_student: () => ({
      colorScheme: "blue",
      children: "student",
    }),
    active_pro: (user) => ({
      colorScheme: "green",
      children: "priority",
    }),
  });

  if (!defaultProps) {
    return null;
  }

  return <Badge {...defaultProps} {...badgeProps} />;
}

export function TripNightsBadge({ availabilityRequest, ...props }) {
  if (!availabilityRequest.isTripPlanner || !availabilityRequest.tripNights) {
    return null;
  }

  const { tripNights } = availabilityRequest;
  const sitesById = React.useMemo(() => {
    const map = new Map();
    if (availabilityRequest.sites) {
      for (const site of availabilityRequest.sites) {
        if (site.name) map.set(site.id, site);
      }
    }
    return map;
  }, [availabilityRequest.sites]);

  const nightCount = tripNights.length;

  // If we can't resolve any site names (e.g. browse page), show count only
  const canResolveNames = tripNights.some((night) =>
    night.siteIds.some((id) => sitesById.has(id)),
  );

  const unit = getCapacityUnit(availabilityRequest);
  const matchStyle = availabilityRequest.allowPartialMatch
    ? "partial match"
    : "exact match";
  const showCapacity = includeCapacityText(availabilityRequest);
  const capacityLabel = showCapacity
    ? `, ${capacityText(availabilityRequest, { unit })}`
    : "";

  if (!canResolveNames) {
    return (
      <Flex alignItems="center" {...props}>
        <Icon as={TripIcon} boxSize="18px" mr={1} />
        <Text>
          {nightCount}-night trip{" "}
          <Text as="span" color="subtle">
            ({matchStyle}
            {capacityLabel})
          </Text>
        </Text>
      </Flex>
    );
  }

  // Resolve names per night
  const nightData = tripNights.map((night, i) => {
    const names = night.siteIds
      .map((id) => sitesById.get(id))
      .filter(Boolean)
      .map((s) => s.name);
    return { index: i, names };
  });

  // Render a single night segment: "Site A" or "Site A (+1)" with tooltip
  const renderNight = (night) => {
    if (night.names.length === 0) return "?";
    if (night.names.length === 1) return night.names[0];
    return (
      <React.Fragment key={night.index}>
        {night.names[0]}{" "}
        <TapTooltip
          label={`Night ${night.index + 1}: ${night.names.join(" or ")}`}
          placement="top"
          hasArrow
        >
          (+{night.names.length - 1})
        </TapTooltip>
      </React.Fragment>
    );
  };

  // Which nights to show inline vs collapse behind "…"
  let visibleNights;
  let hiddenNights = [];
  if (nightCount < 4) {
    visibleNights = nightData;
  } else {
    visibleNights = [nightData[0], nightData[1], nightData[nightCount - 1]];
    hiddenNights = nightData.slice(2, nightCount - 1);
  }

  // Build the route as JSX fragments with arrows
  const routeParts = [];
  visibleNights.forEach((night, i) => {
    if (i > 0) {
      // Insert "…" between second and last when we're truncating
      if (hiddenNights.length > 0 && i === visibleNights.length - 1) {
        const tooltipLabel = hiddenNights
          .map((n) => `Night ${n.index + 1}: ${n.names.join(" or ") || "?"}`)
          .join("\n");
        routeParts.push(
          <React.Fragment key={`ellipsis`}>
            {" → "}
            <TapTooltip
              label={tooltipLabel}
              placement="top"
              hasArrow
              whiteSpace="pre-line"
            >
              …
            </TapTooltip>
            {" → "}
          </React.Fragment>,
        );
      } else {
        routeParts.push(" → ");
      }
    }
    routeParts.push(
      <React.Fragment key={night.index}>{renderNight(night)}</React.Fragment>,
    );
  });

  return (
    <Flex alignItems="flex-start" {...props}>
      <Icon as={TripIcon} boxSize="18px" mr={1} mt="2px" />
      <Text>
        {nightCount}-night trip{" "}
        <Text as="span" color="subtle">
          ({matchStyle}
          {capacityLabel})
        </Text>
        : {routeParts}
      </Text>
    </Flex>
  );
}

export function NewFeatureBadge({ ...badgeProps }) {
  return (
    <Badge colorScheme="green" {...badgeProps}>
      new
    </Badge>
  );
}
